<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div
        v-if="state.isOpen"
        class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50" @click="cancel"></div>

        <!-- Dialog -->
        <div
          class="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6"
          role="alertdialog"
          aria-modal="true"
          :aria-label="title"
        >
          <div class="flex items-start gap-4">
            <!-- Icon -->
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              :class="state.variant === 'danger' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'"
            >
              <svg
                v-if="state.variant === 'danger'"
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-medium text-gray-900">{{ title }}</h3>
              <p class="mt-2 text-sm text-gray-600 whitespace-pre-line">{{ state.message }}</p>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button
              ref="cancelButton"
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
              @click="cancel"
            >
              {{ cancelText }}
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2"
              :class="
                state.variant === 'danger'
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              "
              @click="accept"
            >
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConfirm } from '@/composables/useConfirm'

const { t } = useI18n()
const { state, accept, cancel } = useConfirm()

const cancelButton = ref<HTMLButtonElement | null>(null)

const title = computed(() => state.title || t('common.confirm'))
const confirmText = computed(() => state.confirmText || t('common.confirm'))
const cancelText = computed(() => state.cancelText || t('common.cancel'))

// Focus the cancel button when opened (safer default for destructive prompts)
watch(
  () => state.isOpen,
  async (open) => {
    if (open) {
      await nextTick()
      cancelButton.value?.focus()
    }
  }
)

function onKeydown(e: KeyboardEvent) {
  if (!state.isOpen) return
  if (e.key === 'Escape') cancel()
  else if (e.key === 'Enter') accept()
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.15s ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
</style>
